import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, ExternalLink, Send, FileJson, PartyPopper } from 'lucide-react';
import { api } from '@/services/api';
import { customToast } from '@/components/ui/custom-toast';
import { showCelebrationToast } from '@/components/ui/celebration-toast';
import { useConfetti } from '@/hooks/useConfetti';

interface WorkflowTest {
  webhookUrl: string;
  apiKey: string;
  payload: string;
}

const EXAMPLE_PAYLOADS = {
  scriptBuilder: {
    episodeId: 'ep_test123',
    companyId: 'company_test',
    status: 'draft',
    articleTitle: 'Test Article',
    articleContent: 'This is a test article content for script generation...',
    callbackUrl: 'https://api.mediaorchestrator.com/podcast/callbacks/script'
  },
  audioGenerator: {
    episodeId: 'ep_test123',
    companyId: 'company_test',
    status: 'script_ready',
    script: 'Welcome to today\'s episode. In this episode, we discuss...',
    callbackUrl: 'https://api.mediaorchestrator.com/podcast/callbacks/audio'
  },
  publisher: {
    episodeId: 'ep_test123',
    companyId: 'company_test',
    status: 'ready',
    audioFileUrl: 'https://storage.example.com/audio/ep_test123.mp3',
    callbackUrl: 'https://api.mediaorchestrator.com/podcast/callbacks/publishing'
  }
};

export const ApiTesting: React.FC = () => {
  const { fireConfetti, fireCelebration } = useConfetti();

  // Determine API base URL - now uses media-orchestrator-api
  const getApiBaseUrl = () => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    return isLocalhost ? 'http://localhost:3010' : 'https://api.mediaorchestrator.com';
  };

  const apiBaseUrl = getApiBaseUrl();

  const [workflowTest, setWorkflowTest] = useState<WorkflowTest>({
    webhookUrl: '',
    apiKey: '',
    payload: JSON.stringify(EXAMPLE_PAYLOADS.scriptBuilder, null, 2)
  });

  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [configLoadError, setConfigLoadError] = useState<string | null>(null);

  // Load company configuration on mount
  // Company config is now loaded from the standalone API
  useEffect(() => {
    const loadCompanyConfig = async () => {
      try {
        interface CompanyConfig {
          webhookUrl?: string;
          apiKey?: string;
        }
        const response = await api.get<CompanyConfig>('/companies/current/config');
        if (response.success && response.data) {
          const { webhookUrl, apiKey } = response.data;

          // Pre-populate with company config
          setWorkflowTest(prev => ({
            ...prev,
            webhookUrl: webhookUrl || '',
            apiKey: apiKey || '',
          }));

          setConfigLoadError(null);
        }
      } catch (err: any) {
        console.error('Failed to load company config:', err);

        // Show helpful error message based on status code
        if (err.response?.status === 401) {
          setConfigLoadError('Not authenticated. Please access this page through the parent app to auto-load company settings.');
        } else {
          setConfigLoadError('Failed to load company settings. You can enter values manually below.');
        }
      } finally {
        setLoadingConfig(false);
      }
    };

    loadCompanyConfig();
  }, []);

  const loadExample = (exampleType: keyof typeof EXAMPLE_PAYLOADS) => {
    setWorkflowTest(prev => ({
      ...prev,
      payload: JSON.stringify(EXAMPLE_PAYLOADS[exampleType], null, 2)
    }));
  };

  const testWorkflow = async () => {
    setTesting(true);
    setError(null);
    setTestResult(null);

    try {
      const payload = JSON.parse(workflowTest.payload);
      const response = await api.post('/test/trigger-workflow', {
        webhookUrl: workflowTest.webhookUrl,
        apiKey: workflowTest.apiKey || undefined,
        payload
      });
      setTestResult(response.data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Test failed';
      setError(errorMessage);
      console.error('Test error:', err);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="max-w-content px-8 pb-8 pt-4 min-h-screen">
      <div className="mb-6">
        <h1 className="text-h1 mb-2">API Testing</h1>
        <p className="text-muted-foreground">
          Test Pipedream workflows and explore API documentation
        </p>
      </div>

      <Alert className="mb-6">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          This page is only accessible to super admins. Test configurations are saved locally in your browser.
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="swagger" className="space-y-6">
        <TabsList>
          <TabsTrigger value="swagger">Swagger UI</TabsTrigger>
          <TabsTrigger value="workflow">Workflow Testing</TabsTrigger>
          <TabsTrigger value="toasts">Toast Testing</TabsTrigger>
        </TabsList>

        <TabsContent value="swagger">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Interactive API Documentation</CardTitle>
                  <CardDescription>
                    Explore and test all API endpoints with Swagger UI
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  onClick={() => window.open(`${apiBaseUrl}/docs`, '_blank')}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open in New Tab
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden" style={{ height: '800px' }}>
                <iframe
                  src={`${apiBaseUrl}/docs`}
                  className="w-full h-full"
                  title="Swagger UI"
                  sandbox="allow-same-origin allow-scripts allow-forms"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="workflow" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Test Pipedream Workflow</CardTitle>
              <CardDescription>
                Trigger any Pipedream workflow with custom payload. Use the example dropdown to load preset payloads for different workflow stages.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {configLoadError && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{configLoadError}</AlertDescription>
                </Alert>
              )}

              {/* Read-only webhook settings from company config */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Webhook Configuration</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => window.location.href = '/settings'}
                  >
                    Edit in Settings →
                  </Button>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground min-w-[80px]">URL:</span>
                    <code className="bg-background px-2 py-0.5 rounded text-xs flex-1 truncate">
                      {loadingConfig ? 'Loading...' : (workflowTest.webhookUrl || 'Not configured')}
                    </code>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground min-w-[80px]">API Key:</span>
                    <code className="bg-background px-2 py-0.5 rounded text-xs">
                      {loadingConfig ? 'Loading...' : (workflowTest.apiKey ? '••••••••' : 'Not set')}
                    </code>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="payload">Payload (JSON)</Label>
                  <Select onValueChange={(value) => loadExample(value as keyof typeof EXAMPLE_PAYLOADS)}>
                    <SelectTrigger className="w-[220px]">
                      <SelectValue placeholder="Load example..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scriptBuilder">
                        <div className="flex items-center">
                          <FileJson className="mr-2 h-4 w-4" />
                          Script Builder
                        </div>
                      </SelectItem>
                      <SelectItem value="audioGenerator">
                        <div className="flex items-center">
                          <FileJson className="mr-2 h-4 w-4" />
                          Audio Generator
                        </div>
                      </SelectItem>
                      <SelectItem value="publisher">
                        <div className="flex items-center">
                          <FileJson className="mr-2 h-4 w-4" />
                          Publisher
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Textarea
                  id="payload"
                  rows={14}
                  className="font-mono text-xs"
                  value={workflowTest.payload}
                  onChange={(e) => setWorkflowTest({ ...workflowTest, payload: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Customize the payload as needed. Change the <code className="bg-muted px-1 rounded">status</code> field to test different workflow stages.
                </p>
              </div>

              <Button
                onClick={testWorkflow}
                disabled={testing || !workflowTest.webhookUrl}
              >
                <Send className="mr-2 h-4 w-4" />
                {testing ? 'Testing...' : 'Test Workflow'}
              </Button>
            </CardContent>
          </Card>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {testResult && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Test Result</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded-lg overflow-auto text-xs">
                  {JSON.stringify(testResult, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="toasts" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PartyPopper className="h-5 w-5 text-primary" />
                Celebration Toast
              </CardTitle>
              <CardDescription>
                PodBean publish celebration with confetti burst
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                onClick={() => {
                  showCelebrationToast({
                    title: 'Published to PodBean!',
                    description: 'Episode: "The Future of AI in Media Production"',
                    onClick: () => console.log('Navigating to episode...'),
                    withConfetti: true,
                  });
                }}
                className="w-full"
              >
                Publish to PodBean (Celebration)
              </Button>
              <p className="text-xs text-muted-foreground">
                Triggers confetti + custom celebration toast with cyan glow
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Standard Toasts</CardTitle>
              <CardDescription>
                Default Sonner toast variants
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={() => {
                    customToast.success('Episode saved successfully', {
                      description: 'Your changes have been saved.',
                    });
                  }}
                  variant="outline"
                >
                  Standard Success
                </Button>
                <Button
                  onClick={() => {
                    customToast.error('Failed to save episode', {
                      description: 'Please check your connection and try again.',
                    });
                  }}
                  variant="outline"
                >
                  Standard Error
                </Button>
              </div>
              <Button
                onClick={() => {
                  customToast.success('First toast', { description: 'Testing stack (1 of 3)' });
                  setTimeout(() => {
                    customToast.info('Second toast', { description: 'Testing stack (2 of 3)' });
                  }, 300);
                  setTimeout(() => {
                    customToast.warning('Third toast', { description: 'Testing stack (3 of 3)' });
                  }, 600);
                }}
                variant="outline"
                className="w-full"
              >
                Trigger 3 Toasts (Test Stacking)
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Confetti Only</CardTitle>
              <CardDescription>
                Test confetti effects without toasts
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={() => fireCelebration()} variant="secondary">
                  Full Celebration
                </Button>
                <Button
                  onClick={() => fireConfetti({ particleCount: 50, spread: 60, origin: { x: 0.5, y: 0.3 } })}
                  variant="secondary"
                >
                  Simple Burst
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Configuration</CardTitle>
              <CardDescription>Current toast settings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-muted-foreground">Position:</div>
                <div>top-right</div>
                <div className="text-muted-foreground">Width:</div>
                <div>380px</div>
                <div className="text-muted-foreground">Visible limit:</div>
                <div>4 toasts</div>
                <div className="text-muted-foreground">Default duration:</div>
                <div>5 seconds</div>
                <div className="text-muted-foreground">Celebration duration:</div>
                <div>8 seconds</div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
